import type { FighterDef } from './content'
import type { AttackKind, Combatant, Impact, Intent } from './simulation'

export type LifeFamily='organic'|'xeno'|'revenant'|'rift'|'void'|'crystal'|'wisp'|'celestial'|'tidal'|'plague'
export type BodyRegion='head'|'torso'|'left-arm'|'right-arm'|'left-leg'|'right-leg'
export type OrganRole='control'|'circulation'|'respiration'|'locomotion'|'sensor'|'energy'
export type OrganState={id:string;role:OrganRole;function:number;recovery:number;redundancy:number}
export type ReflexKind='none'|'flinch'|'brace'|'withdraw'|'orient'|'recover'
export type RegionalPain=Record<BodyRegion,number>
export type LivingState={
  family:LifeFamily;organs:OrganState[];regionalPain:RegionalPain
  pain:number;fatigue:number;oxygenation:number;circulation:number;temperature:number
  balance:number;proprioception:number;neuralIntegrity:number;consciousness:number;sensoryConfidence:number
  vision:number;hearing:number;stress:number;recovery:number;electricalLoad:number
  heartbeat:number;breathing:number;reflex:ReflexKind;reflexTimer:number
  lastRegion:BodyRegion;lastOrganId:string|null;eventSerial:number
}
export type LifePresentation={
  pulse:number;breathing:number;tremor:number;electrical:number
  distress:number;organDistress:number;reflex:number
}
export type LifeVisualSignature={pulseRate:number;ringSpin:number;nerveGain:number;breathGain:number}

const clamp01=(value:number)=>Math.max(0,Math.min(1,value))
const toward=(value:number,target:number,rate:number,dt:number)=>
  value+(target-value)*(1-Math.exp(-Math.max(0,rate)*Math.max(0,dt)))
const node=(id:string,role:OrganRole,recovery:number,redundancy=0):OrganState=>
  ({id,role,function:1,recovery,redundancy})

function organBlueprint(family:LifeFamily):OrganState[]{
  switch(family){
    case'xeno':return[
      node('distributed-control','control',.38,2),node('tri-pump','circulation',.34,2),
      node('gas-exchange','respiration',.4,3),node('field-organ','sensor',.55,1),
      node('locomotor-web','locomotion',.3,4),node('bioelectric-core','energy',.44,1)]
    case'crystal':return[
      node('lattice-core','control',.18),node('resonance-web','circulation',.16,6),
      node('prism-sensor','sensor',.28,3),node('fracture-shell','locomotion',.1,8),
      node('crystal-energy-core','energy',.22,2)]
    case'wisp':return[
      node('coherence-core','control',.62),node('field-envelope','circulation',.7),
      node('field-sensor','sensor',.72,2),node('vector-field','locomotion',.68,2),
      node('radiant-core','energy',.74)]
    case'tidal':return[
      node('neural-core','control',.36),node('dual-pump','circulation',.38,1),
      node('gill-array','respiration',.46,5),node('lateral-line','sensor',.58,6),
      node('muscle-web','locomotion',.34,4),node('ionic-core','energy',.42)]
    case'void':return[
      node('phase-core','control',.3,1),node('void-current','circulation',.32,4),
      node('shadow-sensor','sensor',.44,2),node('phase-frame','locomotion',.36,3),
      node('null-core','energy',.4,1)]
    case'revenant':return[
      node('necro-core','control',.24,1),node('ichor-current','circulation',.28,2),
      node('ecto-exchange','respiration',.22,1),node('grave-sense','sensor',.34,2),
      node('bone-muscle','locomotion',.2,5),node('death-energy-core','energy',.3,1)]
    case'rift':return[
      node('rift-control','control',.34,1),node('arcane-circulation','circulation',.32,2),
      node('phase-exchange','respiration',.36,1),node('rift-sense','sensor',.46,2),
      node('phase-locomotion','locomotion',.34,3),node('rift-core','energy',.44,1)]
    case'celestial':return[
      node('luminous-control','control',.4,1),node('radiant-circulation','circulation',.42,2),
      node('aether-exchange','respiration',.4,2),node('luminous-sense','sensor',.5,2),
      node('radiant-locomotion','locomotion',.4,4),node('light-core','energy',.52,1)]
    case'plague':return[
      node('adaptive-control','control',.42,1),node('virulent-circulation','circulation',.48,3),
      node('spore-exchange','respiration',.46,4),node('chemical-sense','sensor',.56,2),
      node('adaptive-locomotion','locomotion',.44,4),node('metabolic-core','energy',.5,1)]
    default:return[
      node('control-core','control',.25),node('circulatory-core','circulation',.2),
      node('respiratory-exchange','respiration',.32,1),node('sensory-array','sensor',.38,2),
      node('musculoskeletal','locomotion',.18,4),node('metabolic-core','energy',.28)]
  }
}

const TRAITS:Record<LifeFamily,{recovery:number;electrical:number;breath:number;oxygen:number;circulation:number}>={
  organic:{recovery:1,electrical:1,breath:1,oxygen:1,circulation:1},
  xeno:{recovery:1.08,electrical:.78,breath:.82,oxygen:.72,circulation:.8},
  revenant:{recovery:.92,electrical:.6,breath:.28,oxygen:.25,circulation:.5},
  rift:{recovery:1.02,electrical:.52,breath:.5,oxygen:.55,circulation:.65},
  void:{recovery:.96,electrical:.42,breath:.38,oxygen:.1,circulation:.4},
  crystal:{recovery:.88,electrical:1.25,breath:.22,oxygen:0,circulation:.45},
  wisp:{recovery:1.16,electrical:.36,breath:.16,oxygen:0,circulation:0},
  celestial:{recovery:1.12,electrical:.62,breath:.62,oxygen:.55,circulation:.7},
  tidal:{recovery:1.06,electrical:1.1,breath:.72,oxygen:.85,circulation:.9},
  plague:{recovery:1.18,electrical:.9,breath:.76,oxygen:.9,circulation:.95},
}
const VISUALS:Record<LifeFamily,LifeVisualSignature>={
  organic:{pulseRate:1,ringSpin:1,nerveGain:.75,breathGain:1},
  xeno:{pulseRate:1.25,ringSpin:1.2,nerveGain:1.05,breathGain:.85},
  revenant:{pulseRate:.72,ringSpin:.8,nerveGain:.7,breathGain:.35},
  rift:{pulseRate:1.35,ringSpin:1.45,nerveGain:1.2,breathGain:.65},
  void:{pulseRate:.62,ringSpin:1.6,nerveGain:1.28,breathGain:.4},
  crystal:{pulseRate:1.6,ringSpin:.68,nerveGain:1.5,breathGain:.25},
  wisp:{pulseRate:1.85,ringSpin:1.8,nerveGain:1.65,breathGain:.2},
  celestial:{pulseRate:1.42,ringSpin:1.35,nerveGain:1.3,breathGain:.7},
  tidal:{pulseRate:.92,ringSpin:.88,nerveGain:1.05,breathGain:.82},
  plague:{pulseRate:1.08,ringSpin:.72,nerveGain:.95,breathGain:.9},
}

export function organFunction(state:LivingState,role:OrganRole){
  const organs=state.organs.filter(item=>item.role===role)
  if(organs.length===0)return 1
  return organs.reduce((sum,item)=>sum+item.function,0)/organs.length
}
const meanOrganFunction=(state:LivingState)=>
  state.organs.length?state.organs.reduce((sum,item)=>sum+item.function,0)/state.organs.length:1
const emptyRegionalPain=():RegionalPain=>({
  'head':0,'torso':0,'left-arm':0,'right-arm':0,'left-leg':0,'right-leg':0
})
const reflexFor=(region:BodyRegion,impact:Impact,kind:AttackKind|null):ReflexKind=>{
  if(impact==='block'||impact==='parry')return'brace'
  if(region==='head')return'flinch'
  if(region==='left-arm'||region==='right-arm'||region==='left-leg'||region==='right-leg')return'withdraw'
  if(kind==='special')return'orient'
  return'flinch'
}

export function lifeFamilyFor(id:string):LifeFamily{
  if(id.includes('alien'))return'xeno'
  if(id.includes('crystal')||id.includes('shatter'))return'crystal'
  if(id.includes('wisp'))return'wisp'
  if(id.includes('tidal')||id.includes('tide'))return'tidal'
  if(id.includes('void')||id.includes('shadow'))return'void'
  if(id.includes('elyon')||id.includes('rift')||id.includes('quantum'))return'rift'
  if(id.includes('celestial')||id.includes('guardian'))return'celestial'
  if(id.includes('revenant'))return'revenant'
  if(id.includes('plague'))return'plague'
  return'organic'
}
export function lifeVisualSignature(family:LifeFamily){return VISUALS[family]}

export function createLivingState(fighterId:string):LivingState{
  const family=lifeFamilyFor(fighterId)
  return{
    family,organs:organBlueprint(family),regionalPain:emptyRegionalPain(),
    pain:0,fatigue:0,oxygenation:1,circulation:1,temperature:.5,balance:1,proprioception:1,
    neuralIntegrity:1,consciousness:1,sensoryConfidence:1,vision:1,hearing:1,stress:.08,
    recovery:1,electricalLoad:0,heartbeat:.34,breathing:.24,reflex:'none',reflexTimer:0,
    lastRegion:'torso',lastOrganId:null,eventSerial:0
  }
}

export function regionForImpact(serial:number,kind:AttackKind|null):BodyRegion{
  const regions:BodyRegion[]=['torso','left-arm','right-arm','left-leg','right-leg','head']
  const bias=kind==='throw'?3:kind==='heavy'?5:kind==='special'?7:1
  return regions[Math.abs((serial*1103515245+bias)>>>0)%regions.length]
}
export function organRoleForImpact(region:BodyRegion,kind:AttackKind|null,serial:number):OrganRole{
  if(region==='left-leg'||region==='right-leg'||region==='left-arm'||region==='right-arm')return'locomotion'
  if(region==='head')return serial%2===0?'control':'sensor'
  if(kind==='special')return'energy'
  const torso:OrganRole[]=['circulation','respiration','energy']
  return torso[Math.abs(serial)%torso.length]
}
function damageOrgan(state:LivingState,role:OrganRole,severity:number,serial:number){
  const candidates=state.organs.map((organ,index)=>({organ,index})).filter(item=>item.organ.role===role)
  if(candidates.length===0)return{organs:state.organs,lastOrganId:null as string|null}
  const target=candidates[Math.abs(serial)%candidates.length]
  const protection=1+target.organ.redundancy*.16
  const damage=severity*(role==='control'?.68:role==='locomotion'?.58:.52)/protection
  const organs=state.organs.map((organ,index)=>
    index===target.index?{...organ,function:clamp01(organ.function-damage)}:organ)
  return{organs,lastOrganId:target.organ.id}
}

export function applyLivingImpact(state:LivingState,impact:Impact,kind:AttackKind|null,serial:number,contactRegion?:BodyRegion,contactRole?:OrganRole):LivingState{
  if(impact==='whiff')return state
  const base=kind==='special'?.27:kind==='heavy'?.2:kind==='throw'?.17:.1
  const severity=base*(impact==='block'?.35:impact==='parry'?.62:impact==='guard-break'?.92:1)
  const region=contactRegion??regionForImpact(serial,kind),trait=TRAITS[state.family]
  const role=contactRole??organRoleForImpact(region,kind,serial),organHit=damageOrgan(state,role,severity,serial)
  const organState={...state,organs:organHit.organs}
  const electrical=kind==='special'?severity*trait.electrical:.02*severity
  const leg=region==='left-leg'||region==='right-leg',head=region==='head'
  const control=organFunction(organState,'control'),sensor=organFunction(organState,'sensor')
  const circulationNode=organFunction(organState,'circulation'),respiration=organFunction(organState,'respiration')
  const locomotion=organFunction(organState,'locomotion'),energy=organFunction(organState,'energy')
  const vision=Math.min(clamp01(state.vision-(head?severity*.16:0)),clamp01(.15+sensor*.85))
  const hearing=Math.min(clamp01(state.hearing-(head?severity*.09:0)),clamp01(.2+sensor*.8))
  const neuralIntegrity=Math.min(
    clamp01(state.neuralIntegrity-electrical*.34-(head?severity*.08:0)),
    clamp01(control*.9+energy*.1))
  const regionalPain={...state.regionalPain,[region]:clamp01(state.regionalPain[region]+severity*(impact==='block'?.45:1))}
  const consciousness=Math.min(
    state.consciousness,
    clamp01(.35+control*.35+state.oxygenation*.15+state.circulation*.15-(head?severity*.12:0)))
  const sensoryConfidence=clamp01(vision*.42+hearing*.28+control*.18+neuralIntegrity*.12)
  return{
    ...state,organs:organHit.organs,regionalPain,
    pain:clamp01(state.pain+severity),stress:clamp01(state.stress+severity*.78),
    temperature:clamp01(state.temperature+(kind==='special'?severity*.04:0)),
    balance:Math.min(clamp01(state.balance-severity*(leg?.7:head?.58:.28)),clamp01(locomotion*.72+sensor*.28)),
    proprioception:Math.min(clamp01(state.proprioception-severity*(leg?.55:.2)),clamp01(locomotion*.8+control*.2)),
    neuralIntegrity,consciousness,sensoryConfidence,vision,hearing,
    circulation:Math.min(state.circulation,clamp01((1-trait.circulation)+circulationNode*trait.circulation)),
    oxygenation:Math.min(state.oxygenation,clamp01((1-trait.oxygen)+respiration*trait.oxygen)),
    electricalLoad:clamp01(state.electricalLoad+electrical),
    recovery:clamp01(state.recovery-severity*(.22+energy*.13)),
    reflex:reflexFor(region,impact,kind),reflexTimer:impact==='block'?.12:kind==='special'?.28:.18,
    lastRegion:region,lastOrganId:organHit.lastOrganId,eventSerial:serial
  }
}

export function tickLivingState(state:LivingState,combatant:Combatant,dt:number):LivingState{
  const safeDt=Math.max(0,Math.min(Number.isFinite(dt)?dt:0,.05)),trait=TRAITS[state.family]
  const organs=state.organs.map(organ=>({
    ...organ,function:toward(organ.function,1,organ.recovery*trait.recovery*(.25+state.recovery*.75),safeDt)
  }))
  const organState={...state,organs}
  const healthRatio=combatant.maxHealth>0?clamp01(combatant.health/combatant.maxHealth):0
  const injury=1-healthRatio,exertion=clamp01(1-combatant.stamina/100)
  const control=organFunction(organState,'control'),sensor=organFunction(organState,'sensor')
  const circulationOrgan=organFunction(organState,'circulation'),respiration=organFunction(organState,'respiration')
  const locomotion=organFunction(organState,'locomotion'),energy=organFunction(organState,'energy')
  const downStress=combatant.downed?.94:combatant.hitstun>0?.62:0
  const painFloor=injury*.36,fatigueTarget=clamp01(exertion*.78+injury*.2+(1-locomotion)*.24)
  const oxygenTarget=clamp01((1-trait.oxygen)+(1-exertion*.3-(combatant.downed?.12:0))*respiration*trait.oxygen)
  const circulationTarget=clamp01((1-trait.circulation)+(1-injury*.34)*circulationOrgan*trait.circulation)
  const stressTarget=clamp01(Math.max(.06,injury*.68+exertion*.22+(1-control)*.28,downStress))
  const recoveryTarget=clamp01((1-injury*.44-exertion*.2)*(.68+energy*.32))
  const electricalLoad=toward(state.electricalLoad,0,.9*trait.recovery,safeDt)
  const neuralTarget=clamp01(control*.9+energy*.1)
  const neuralIntegrity=toward(
    state.neuralIntegrity,neuralTarget,1.05*trait.recovery*(1-electricalLoad*.45),safeDt)
  const balanceTarget=combatant.downed?0:combatant.hitstun>0?.48:
    clamp01(.62+neuralIntegrity*.16+sensor*.1+locomotion*.12)
  const proprioTarget=combatant.downed?.42:clamp01(.6+neuralIntegrity*.14+locomotion*.26)
  const pain=Math.max(painFloor,toward(state.pain,painFloor,.62*trait.recovery,safeDt))
  const fatigue=toward(state.fatigue,fatigueTarget,1.8,safeDt)
  const stress=toward(state.stress,stressTarget,2.2,safeDt)
  const oxygenation=toward(state.oxygenation,oxygenTarget,2.6*trait.recovery,safeDt)
  const circulation=toward(state.circulation,circulationTarget,1.5*trait.recovery,safeDt)
  const balance=toward(state.balance,balanceTarget,2.9*trait.recovery,safeDt)
  const proprioception=toward(state.proprioception,proprioTarget,2.4*trait.recovery,safeDt)
  const recovery=toward(state.recovery,recoveryTarget,1.25*trait.recovery,safeDt)
  const visionTarget=clamp01((.15+sensor*.85)*(1-injury*.08))
  const hearingTarget=clamp01((.2+sensor*.8)*(1-injury*.04))
  const vision=toward(state.vision,visionTarget,.72*trait.recovery,safeDt)
  const hearing=toward(state.hearing,hearingTarget,.82*trait.recovery,safeDt)
  const regionalPain:RegionalPain={
    'head':toward(state.regionalPain['head'],injury*.08,.7*trait.recovery,safeDt),
    'torso':toward(state.regionalPain['torso'],injury*.12,.64*trait.recovery,safeDt),
    'left-arm':toward(state.regionalPain['left-arm'],injury*.05,.78*trait.recovery,safeDt),
    'right-arm':toward(state.regionalPain['right-arm'],injury*.05,.78*trait.recovery,safeDt),
    'left-leg':toward(state.regionalPain['left-leg'],injury*.06,.72*trait.recovery,safeDt),
    'right-leg':toward(state.regionalPain['right-leg'],injury*.06,.72*trait.recovery,safeDt)
  }
  const temperatureTarget=clamp01(.5+exertion*.22+(1-energy)*.08+electricalLoad*.04)
  const temperature=toward(state.temperature,temperatureTarget,1.2*trait.recovery,safeDt)
  const consciousnessTarget=clamp01(.25+control*.32+neuralIntegrity*.2+oxygenation*.12+circulation*.11)
  const consciousness=toward(state.consciousness,consciousnessTarget,1.8*trait.recovery,safeDt)
  const sensoryConfidence=clamp01(
    vision*.42+hearing*.28+sensor*.12+neuralIntegrity*.12+consciousness*.06)
  const reflexTimer=Math.max(0,state.reflexTimer-safeDt)
  const reflex:ReflexKind=reflexTimer>0?state.reflex:'none'
  const heartbeat=clamp01(.3+stress*.42+fatigue*.28+injury*.12+(1-circulationOrgan)*.18)
  const breathing=clamp01((.18+fatigue*.62+stress*.2+(1-respiration)*.24)*trait.breath)
  return{
    ...state,organs,regionalPain,pain,fatigue,oxygenation,circulation,temperature,
    balance,proprioception,neuralIntegrity,consciousness,sensoryConfidence,
    vision,hearing,stress,recovery,electricalLoad,heartbeat,breathing,reflex,reflexTimer
  }
}
export function perceptionConfidence(state:LivingState){
  return clamp01(
    state.sensoryConfidence*.6+
    state.consciousness*.15+
    state.neuralIntegrity*.15+
    organFunction(state,'sensor')*.1)
}
export function applyPerceptionToIntent(intent:Intent,state:LivingState,tick:number):Intent{
  const confidence=perceptionConfidence(state)
  if(confidence>=.68)return intent
  const cautious={...intent,dash:confidence>.38&&intent.dash}
  if(confidence<.28&&tick%4!==0){
    return{...cautious,light:false,heavy:false,special:false,throw:false}
  }
  if(confidence<.48&&tick%3===1){
    return{...cautious,heavy:false,special:false,throw:false}
  }
  return cautious
}

export function lifeModifiers(state:LivingState){
  const locomotion=organFunction(state,'locomotion'),control=organFunction(state,'control')
  const legPain=Math.max(state.regionalPain['left-leg'],state.regionalPain['right-leg'])
  const armPain=Math.max(state.regionalPain['left-arm'],state.regionalPain['right-arm'])
  const mobility=clamp01(
    state.balance*.31+state.proprioception*.23+locomotion*.2+(1-state.fatigue)*.16+
    state.consciousness*.1-legPain*.08)
  const neural=clamp01(
    state.neuralIntegrity*.58+control*.17+(1-state.pain)*.08+
    state.oxygenation*.07+state.consciousness*.1-armPain*.05)
  return{speed:.86+mobility*.14,power:.9+neural*.1}
}
export function applyLivingModifiers(combatant:Combatant,definition:FighterDef,state:LivingState):Combatant{
  const modifier=lifeModifiers(state)
  return{...combatant,speed:definition.speed*modifier.speed,power:definition.power*modifier.power}
}
export function lifePresentation(state:LivingState):LifePresentation{
  const organDistress=clamp01(1-meanOrganFunction(state))
  const regionalDistress=Math.max(...Object.values(state.regionalPain))
  return{
    pulse:clamp01(.25+state.heartbeat*.75),
    breathing:clamp01(state.breathing),
    tremor:clamp01(
      state.pain*.26+(1-state.neuralIntegrity)*.48+state.stress*.12+
      organDistress*.18+regionalDistress*.12+(1-state.consciousness)*.14),
    electrical:clamp01(state.electricalLoad+(1-state.neuralIntegrity)*.35),
    distress:clamp01(
      state.pain*.3+state.fatigue*.16+state.stress*.14+(1-state.balance)*.08+
      organDistress*.2+regionalDistress*.12+(1-state.consciousness)*.12),
    organDistress,
    reflex:state.reflexTimer>0?clamp01(state.reflexTimer/.28):0
  }
}
export type LifeSimulationTier='T0'|'T1'|'T2'|'T3'|'T4'
export function lifeSimulationTier(input:{hero?:boolean;combatRelevant?:boolean;visible?:boolean;distance:number}):LifeSimulationTier{
  if(input.hero)return'T0'
  if(input.combatRelevant)return'T1'
  if(input.visible&&input.distance<=32)return'T2'
  if(input.distance<=90)return'T3'
  return'T4'
}
export function lifeSimulationHz(tier:LifeSimulationTier){
  switch(tier){case'T0':return 60;case'T1':return 30;case'T2':return 10;case'T3':return 2;case'T4':return .25}
}
export function shouldUpdateLife(tick:number,tier:LifeSimulationTier,baseHz=60){
  const hz=lifeSimulationHz(tier),stride=Math.max(1,Math.round(baseHz/hz))
  return Math.abs(tick)%stride===0
}
export type LifeTelemetry={
  family:LifeFamily;tier:LifeSimulationTier;healthState:number;organIntegrity:number
  sensoryConfidence:number;consciousness:number;pain:number;fatigue:number
  oxygenation:number;circulation:number;balance:number;neuralIntegrity:number
  temperature:number;electricalLoad:number;criticalOrgan:string|null;reflex:ReflexKind
}
export function lifeTelemetry(state:LivingState,tier:LifeSimulationTier='T0'):LifeTelemetry{
  const critical=state.organs.reduce<OrganState|null>((worst,organ)=>!worst||organ.function<worst.function?organ:worst,null)
  return{
    family:state.family,tier,healthState:clamp01((state.consciousness+state.neuralIntegrity+state.oxygenation+state.circulation)/4),
    organIntegrity:clamp01(meanOrganFunction(state)),sensoryConfidence:perceptionConfidence(state),
    consciousness:state.consciousness,pain:state.pain,fatigue:state.fatigue,
    oxygenation:state.oxygenation,circulation:state.circulation,balance:state.balance,
    neuralIntegrity:state.neuralIntegrity,temperature:state.temperature,electricalLoad:state.electricalLoad,
    criticalOrgan:critical?.id??null,reflex:state.reflex
  }
}

